import { getLocaleMeta } from "@/lib/i18n/locales";

/**
 * Write a number the way the reader's language writes one.
 *
 * The counterpart to `parse-number.ts`, and the half that decides whether the other half
 * is coherent. A dozen widgets computed a result and rendered it through
 * `toLocaleString("en-US")` — a hard-coded English number in German, French and Russian
 * prose, on pages that are now published in all three. `1,234.5` is not a number a
 * German reader recognises; `1.234,5` is.
 *
 * It matters more than a formatting nicety because these tools chain. A reader converts
 * a fraction to a decimal in one tool and types the answer into the next one, and until
 * `parse-number.ts` landed, a comma-decimal answer would have been misread by every
 * field on the site. Reading and writing had to move together: writing `2,5` while only
 * accepting `2.5` would have been worse than the English number it replaced.
 *
 * ## Why not `formatCount`
 *
 * `formatCount` in `@/lib/i18n` does the same job and is the right call in a server
 * component. This exists because `@/lib/i18n/index.ts` pulls in `getDictionary`, which
 * imports all seventeen dictionaries — fine on a server, a large thing to drag into a
 * client bundle for one call to `Intl`. This module imports only the locale table.
 *
 * ## Scope
 *
 * For a number the reader READS. Not for one that goes back into a machine: a value
 * embedded in a URL, a CSV cell, a JSON payload or an `Intl.DateTimeFormat` part being
 * re-parsed must stay ASCII with a period, and `styleCountText()` is the documented
 * accessor for the machine surfaces that quote the catalog total.
 *
 * Not for a LIST of numbers rendered comma-separated either. `1,5, 2,5, 3,5` is unreadable
 * and unparseable, and a tool that emits one has to pick a separator that its own input
 * side would accept — a semicolon, per `parseNumberList`. Formatting the members of a
 * list one at a time is the trap this note exists to name.
 */
export function formatNumber(
  locale: string,
  value: number,
  options?: Intl.NumberFormatOptions
): string {
  const tag = getLocaleMeta(locale)?.hreflang ?? "en-US";
  try {
    return value.toLocaleString(tag, options);
  } catch {
    // An Intl build without this locale's data must not take a tool down mid-keystroke.
    return value.toLocaleString("en-US", options);
  }
}

/**
 * A date and time the reader reads.
 *
 * Same rule and the same exception: the `Intl.DateTimeFormat("en-US")` calls that exist
 * to pull numeric PARTS back out of a formatted date are machine plumbing and must keep
 * their fixed locale, or the parse breaks in the languages whose month names differ.
 */
export function formatDateTime(
  locale: string,
  value: Date,
  options?: Intl.DateTimeFormatOptions
): string {
  const tag = getLocaleMeta(locale)?.hreflang ?? "en-US";
  try {
    return value.toLocaleString(tag, options);
  } catch {
    return value.toLocaleString("en-US", options);
  }
}
